/**
 * Calculation Methods Guide — flat sections (no accordion)
 */
import React from 'react';
import {
  GuideSection,
  GuideSubtitle,
  GuideFormula,
  GuideExample,
  GuideCallout,
  GuideMethodCard,
  GuideTable,
} from '../guide/GuideLayout';
import { BRAND } from '../../theme/brandTheme';

export const METHODS_GUIDE_TOC = [
  { id: 'methods', label: 'Yöntemler' },
  { id: 'bayesian', label: 'Bayesian' },
  { id: 'samplesize', label: 'Sample Size' },
  { id: 'duration', label: 'Test Süresi' },
  { id: 'significance', label: 'Anlamlılık' },
  { id: 'practices', label: 'En İyi Uygulamalar' },
];

const CalculationMethodsGuide = () => (
  <>
    <GuideSection
      id="methods"
      number="01"
      title="İstatistiksel Analiz Yöntemleri"
      lead="Hesaplayıcıda kullanılan üç yöntemin kısa karşılaştırması. Çok varyantlı testlerde Bayesian önerilir."
    >
      <div className="guide-method-grid">
        <GuideMethodCard
          title="Bayesian Analysis"
          description="Posterior dağılım ve Monte Carlo ile kazanma olasılığı hesaplar."
          pros={[
            'Kazanma olasılığını doğrudan verir',
            'Çoklu varyant karşılaştırması',
            'Probability density grafiği',
          ]}
          cons={['Daha yüksek hesaplama maliyeti', 'Prior seçimi subjektif olabilir']}
        />
        <GuideMethodCard
          title="Z-Test"
          description="İki oranın karşılaştırılması için klasik test; p-value üretir."
          pros={['Hızlı ve yaygın', 'Güven aralığı verir', 'Matematiksel olarak sağlam']}
          cons={['Sadece ikili karşılaştırma', 'Kazanma olasılığı vermez']}
        />
        <GuideMethodCard
          title="Chi-Squared Test"
          description="Kategorik verilerde gözlenen ve beklenen frekansları karşılaştırır."
          pros={['Kategorik veriler için ideal', 'Non-parametrik', 'Bağımsızlık testi']}
          cons={['Beklenen hücre ≥5 kuralı', 'Etki büyüklüğü vermez']}
        />
      </div>
    </GuideSection>

    <GuideSection
      id="bayesian"
      number="02"
      title="Bayesian Analysis"
      lead="Beta posterior ve Monte Carlo simülasyonu ile varyantların kazanma olasılığı hesaplanır."
    >
      <GuideSubtitle>Temel formüller</GuideSubtitle>
      <GuideFormula
        label="Beta posterior"
        formula="Beta(α + conversions, β + sessions − conversions)"
        description="α = 1, β = 1 non-informative prior kullanılır."
      />
      <GuideFormula
        label="Monte Carlo kazanma olasılığı"
        formula={`for i in 1..100000:
  control_sample = Beta(α_c, β_c)
  variant_sample = Beta(α_v, β_v)
  if variant_sample > control_sample: wins += 1
probability = wins / 100000`}
        description="100.000 iterasyon ile simülasyon yapılır."
      />
      <GuideCallout variant="info">
        <strong>CXL uyumluluğu:</strong> Tüm varyantlar birlikte karşılaştırılır; kazanma olasılıkları toplamı %100 olur.
      </GuideCallout>

      <GuideSubtitle>Probability density</GuideSubtitle>
      <GuideFormula
        label="Beta PDF"
        formula="f(x|α,β) = (Γ(α+β) / (Γ(α)Γ(β))) × x^(α−1) × (1−x)^(β−1)"
        description="Dönüşüm oranı dağılımının yoğunluk fonksiyonu."
      />
      <ol className="guide-steps">
        <li>Posterior parametreler: α = 1 + conversions, β = 1 + (sessions − conversions)</li>
        <li>0–1 aralığında 200 nokta için Beta PDF hesaplanır</li>
        <li>Control ve varyant dağılımları grafikte karşılaştırılır</li>
      </ol>
    </GuideSection>

    <GuideSection
      id="samplesize"
      number="03"
      title="Sample Size Hesaplama"
      lead="Test planlama sekmesinde minimum örneklem büyüklüğü bu formülle tahmin edilir."
    >
      <GuideFormula
        label="Sample size"
        formula={`n = (Z_α/2 × √(2p̄(1−p̄)) + Z_β × √(p₁(1−p₁) + p₂(1−p₂)))² / (p₂ − p₁)²

p₁ = baseline CR · p₂ = p₁ × (1 + MDE) · p̄ = (p₁ + p₂) / 2`}
        description="Her varyant için gerekli ziyaretçi sayısı."
      />
      <GuideTable
        columns={['Parametre', 'Açıklama', 'Tipik değer']}
        rows={[
          [<strong>α</strong>, 'Type I error (yanlış pozitif)', '0.05 → %95 güven'],
          [<strong>β</strong>, 'Type II error (yanlış negatif)', '0.20 → %80 power'],
          [<strong>MDE</strong>, 'Minimum detectable effect', '%10–30 relative lift'],
          [<strong>Baseline</strong>, 'Mevcut dönüşüm oranı', 'Siteye özel'],
        ]}
      />
      <GuideExample title="Örnek: baseline %5, MDE %20">
        p₁ = 0.05 → p₂ = 0.06 → n ≈ <strong>3.842</strong> (varyant başına)
      </GuideExample>
      <GuideCallout variant="warn">
        Gerçek hesaplamada ek düzeltmeler uygulanabilir; sonuç yaklaşık değerdir.
      </GuideCallout>
    </GuideSection>

    <GuideSection
      id="duration"
      number="04"
      title="Test Süresi ve Ek Metrikler"
      lead="Sample size, trafik ve test süresinden günlük/aylık/yıllık projeksiyonlar türetilir."
    >
      <GuideFormula
        label="Test süresi"
        formula={`Süre (gün) = Sample Size / (Günlük Trafik × Trafik Payı)

Örnek: 10.000 / (1.000 × 0.5) = 20 gün`}
        description="Haftalık döngüleri yakalamak için minimum 7 gün önerilir."
      />
      <GuideFormula
        label="Ek gün ihtiyacı"
        formula="Ek Gün = (Gerekli Örneklem − Mevcut Örneklem) / Günlük Test Trafiği"
      />
      <GuideFormula
        label="Ek işlem & gelir"
        formula={`Lift = Varyant CR − Kontrol CR
Günlük Ek = Günlük Trafik × Trafik Payı × Lift
Aylık Ek = Günlük Ek × 30 · Yıllık Ek = Günlük Ek × 365
Gelir = Ek İşlem × AOV`}
      />
      <GuideExample title="Örnek projeksiyon">
        Lift +1.5 puan · 1.000 ziyaret/gün · %50 trafik → <strong>15 ek dönüşüm/gün</strong>
        <br />
        AOV 100₺ → ~45.000₺/ay ek gelir potansiyeli
      </GuideExample>
    </GuideSection>

    <GuideSection
      id="significance"
      number="05"
      title="Anlamlılık vs Kazanma Olasılığı"
      lead="Significance 'fark gerçek mi?' sorusunu; kazanma olasılığı 'hangi varyant kazanır?' sorusunu yanıtlar."
    >
      <GuideFormula
        label="Z-Test p-value"
        formula={`Z = (p₁ − p₂) / SE · SE = √(p̄(1−p̄)(1/n₁ + 1/n₂))
Anlamlı if p-value < α`}
      />
      <GuideFormula
        label="Chi-Squared"
        formula="χ² = Σ((Gözlenen − Beklenen)² / Beklenen)"
      />
      <GuideFormula
        label="Çoklu varyant (Bayesian)"
        formula={`Her iterasyonda tüm varyantlardan Beta örneği alınır
Kazanan = argmax(samples) · Olasılıklar toplamı = %100`}
      />
      <GuideTable
        columns={['', 'Significance', 'Kazanma olasılığı']}
        rows={[
          ['Çıktı', 'Significant / Not', '%11, %10, %79'],
          ['Toplam', '—', '%100'],
          ['Yöntem', 'P-value', 'Monte Carlo'],
        ]}
      />
    </GuideSection>

    <GuideSection
      id="practices"
      number="06"
      title="En İyi Uygulamalar"
      lead="Test planlama ve analiz yöntemi seçimi için pratik öneriler."
    >
      <GuideSubtitle>Test planlama</GuideSubtitle>
      <ul className="guide-steps">
        <li>Minimum <strong>7 gün</strong> test süresi (haftalık döngü)</li>
        <li><strong>%95 güven</strong> seviyesi (endüstri standardı)</li>
        <li><strong>%80 statistical power</strong> hedefleyin</li>
        <li>Gerçekçi MDE: %10–30 relative lift</li>
      </ul>

      <GuideSubtitle>Yöntem seçimi</GuideSubtitle>
      <GuideCallout variant="info">
        <strong>2 varyant:</strong> Z-Test veya Bayesian · <strong>3+ varyant:</strong> Bayesian ·{' '}
        <strong>Sürekli izleme:</strong> Bayesian
      </GuideCallout>

      <p style={{ fontFamily: BRAND.fontFamily, fontSize: 14, color: BRAND.textMuted, marginTop: 16 }}>
        Chi-Squared kategorik bağımsızlık testlerinde; Z-Test hızlı ikili karşılaştırmalarda tercih edilebilir.
      </p>
    </GuideSection>
  </>
);

export default CalculationMethodsGuide;
